import Vue from "vue";
import VueRouter from "vue-router";
import Guard from "@/helpers/guards";
import auth from "@/helpers/auth";
import store from "@/store";
import { hasSolarActionsDevice } from "@/helpers/solarActionsAccess";
import { logRouteGuardRejection } from "@apps-logs/vue2";
import logger, { logAuthEvent } from "@/plugins/appsLogsCore";

Vue.use(VueRouter);

const Groups = () =>
  import(
    /* webpackChunkName: "route-groups" */ "@/components/groups/Groups.vue"
  );
const GroupDetail = () =>
  import(
    /* webpackChunkName: "route-group-detail" */ "@/components/groups/GroupDetail.vue"
  );
const GroupDevices = () =>
  import(
    /* webpackChunkName: "route-group-devices" */ "@/components/groups/GroupDevices.vue"
  );
const SubGroups = () =>
  import(
    /* webpackChunkName: "route-sub-groups" */ "@/components/groups/SubGroups.vue"
  );
const EditGroups = () =>
  import(
    /* webpackChunkName: "route-edit-groups" */ "@/components/groups/EditGroups.vue"
  );
const Main = () =>
  import(
    /* webpackChunkName: "route-dashboard" */ "@/components/devices/Main.vue"
  );
const Devices = () =>
  import(
    /* webpackChunkName: "route-devices" */ "@/components/devices/Devices.vue"
  );
const DevicesExceeds = () =>
  import(
    /* webpackChunkName: "route-devices-exceeds" */ "@/components/devices/DevicesExceeds.vue"
  );
const Device = () =>
  import(
    /* webpackChunkName: "route-device" */ "@/components/devices/device.vue"
  );
const Automations = () =>
  import(
    /* webpackChunkName: "route-automations" */ "@/components/devices/automations.vue"
  );
const Signin = () =>
  import(/* webpackChunkName: "route-signin" */ "../views/user/Signin");
const NewDevice = () =>
  import(
    /* webpackChunkName: "route-new-device" */ "@/views/devices/NewDevice.vue"
  );
const NewGateway = () =>
  import(
    /* webpackChunkName: "route-new-gateway" */ "@/views/devices/NewGateway.vue"
  );
const Logs = () =>
  import(/* webpackChunkName: "route-logs" */ "@/components/logs/create.vue");
const Notifications = () =>
  import(
    /* webpackChunkName: "route-notifications" */ "@/components/User/notifications.vue"
  );
const SolarActionsPage = () =>
  import(
    /* webpackChunkName: "route-solar-actions" */ "@/components/solar-actions/SolarActionsPage.vue"
  );
const Ottr = () =>
  import(/* webpackChunkName: "route-one-time-token" */ "@/views/ottr.vue");

const ignoredNavigationFailureNames = [
  "NavigationDuplicated",
  "NavigationRedirected",
  "NavigationCancelled",
  "NavigationAborted",
];

const isIgnoredNavigationFailure = (error) => {
  if (!error) {
    return false;
  }

  if (VueRouter.isNavigationFailure && VueRouter.isNavigationFailure(error)) {
    return true;
  }

  return ignoredNavigationFailureNames.includes(error.name);
};

async function hasSolarActionsAccess() {
  let devicesMap = store.getters["devices/alldevices"] || {};

  if (!devicesMap || !Object.keys(devicesMap).length) {
    await store.dispatch("devices/fetchDevices");
    devicesMap = store.getters["devices/alldevices"] || {};
  }

  return hasSolarActionsDevice(devicesMap);
}

function routeGuardMetadata(to, from, extra = {}) {
  return {
    toName: to && to.name,
    toPath: to && to.path,
    toFullPath: to && to.fullPath,
    fromName: from && from.name,
    fromPath: from && from.path,
    fromFullPath: from && from.fullPath,
    requiresAuth: !!(to && to.meta && to.meta.requiresAuth),
    requiresGuest: !!(to && to.meta && to.meta.requiresGuest),
    permissionsRequired:
      to && to.meta && to.meta.permissionsRequired
        ? JSON.stringify(to.meta.permissionsRequired)
        : undefined,
    isAuthenticated: Guard.validUser(),
    ...extra,
  };
}

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }

  return originalPush.call(this, location).catch((error) => {
    if (isIgnoredNavigationFailure(error)) {
      return error;
    }

    throw error;
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject);
  }

  return originalReplace.call(this, location).catch((error) => {
    if (isIgnoredNavigationFailure(error)) {
      return error;
    }

    throw error;
  });
};

const routes = [
  {
    path: "/login",
    name: "signin",
    component: Signin,
    meta: { requiresGuest: true, screenName: "Login" },
  },
  {
    path: "/sign-up",
    name: "signup",
    component: () =>
      import(/* webpackChunkName: "route-signup" */ "@/views/user/Signup"),
    meta: { requiresGuest: true, screenName: "Sign Up" },
  },
  {
    path: "/profile",
    name: "profile",
    component: () =>
      import(
        /* webpackChunkName: "route-profile" */ "@/components/User/profile"
      ),
    meta: { requiresAuth: true, screenName: "Profile" },
  },
  {
    path: "/map",
    name: "map",
    component: () =>
      import(
        /* webpackChunkName: "route-map" */ "@/components/map/GlobalMapPage"
      ),
    meta: { requiresAuth: true, screenName: "Map" },
  },
  {
    path: "/gateways",
    name: "GatewaysPage",
    component: () =>
      import(
        /* webpackChunkName: "route-gateways" */ "@/components/devices/GatewaysPage"
      ),
    meta: { requiresAuth: true, screenName: "Gateways" },
  },
  {
    path: "/gateways/:identifier",
    name: "GatewayPage",
    component: () =>
      import(
        /* webpackChunkName: "route-gateway" */ "@/components/devices/gateway"
      ),
    meta: { requiresAuth: true, screenName: "Gateway Details" },
  },
  {
    path: "/profile/Settings",
    name: "settings",
    component: () =>
      import(
        /* webpackChunkName: "route-settings" */ "@/components/Settings/settings"
      ),
    meta: { requiresAuth: true, screenName: "Settings", kioskBlocked: true },
  },
  {
    path: "/profile/notifications",
    name: "notifications",
    component: Notifications,
    meta: { requiresAuth: true, screenName: "Notifications" },
  },
  {
    path: "/",
    name: "landing",
    component: { render: (h) => h() },
    meta: { requiresAuth: true, screenName: "Landing" },
  },
  {
    path: "/dashboard",
    name: "dashboard",
    component: Main,
    meta: { requiresAuth: true, screenName: "Dashboard" },
  },

  {
    path: "/devices/exceeds",
    name: "devicesExceeds",
    component: DevicesExceeds,
    meta: { requiresAuth: true, screenName: "Devices – Exceeds Limit" },
  },
  {
    path: "/devices",
    name: "devices",
    component: Devices,
    meta: { requiresAuth: true, screenName: "Devices" },
  },
  {
    path: "/devices/:slug",
    name: "device",
    component: Device,
    meta: { requiresAuth: true, screenName: "Device Details" },
  },
  {
    path: "/new-device/new",
    name: "new-device",
    component: NewDevice,
    meta: { requiresAuth: true, screenName: "Add Device", kioskBlocked: true },
  },
  {
    path: "/new-gateway/new",
    name: "new-gateway",
    component: NewGateway,
    meta: { requiresAuth: true, screenName: "Add Gateway", kioskBlocked: true },
  },
  {
    path: "/edit-view",
    name: "edit-view",
    component: () =>
      import(
        /* webpackChunkName: "route-edit-view" */ "@/components/devices/editview"
      ),
    meta: { requiresAuth: true, screenName: "Edit View", kioskBlocked: true },
  },
  {
    path: "/groups",
    name: "groups",
    component: Groups,
    meta: { requiresAuth: true, screenName: "Groups" },
  },

  {
    path: "/groups/:gslug/sub-groups",
    name: "group-groups",
    component: SubGroups,
    meta: { requiresAuth: true, screenName: "Sub-Groups" },
    // children: [
    //   {
    //     path: "/groups/:gs/sub-groups/:sgs",
    //     name: "InsideSub",
    //     component: SubGroups,
    //     meta: { requiresAuth: true },
    //   },
    // ],
  },
  // {
  //   path: "/groups/:gslug/sub-group",
  //   name: "InsideSub",
  //   component: SubGroups,
  //   meta: { requiresAuth: true },
  // },
  {
    path: "/logs",
    name: "logs",
    component: Logs,
    meta: { requiresAuth: true, screenName: "Logs" },
  },
  {
    path: "/automations",
    name: "automations",
    component: Automations,
    meta: {
      requiresAuth: true,
      permissionsRequired: ["automations"],
      screenName: "Automations",
    },
  },
  {
    path: "/solar-actions",
    name: "solar-actions",
    component: SolarActionsPage,
    meta: {
      requiresAuth: true,
      requiresSolarActionsDevice: true,
      screenName: "Solar Actions",
    },
  },
  {
    path: "/solar-actions/:actionId",
    name: "solar-action-details",
    component: () =>
      import(
        /* webpackChunkName: "route-solar-action-details" */ "@/components/solar-actions/SolarActionDetailsPage.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresSolarActionsDevice: true,
      screenName: "Solar Action Details",
    },
  },
  {
    path: "/groups/edit",
    name: "edit-groups",
    component: EditGroups,
    meta: { requiresAuth: true, screenName: "Edit Groups", kioskBlocked: true },
  },
  {
    path: "/groups/:gslug",
    name: "group-detail",
    component: GroupDetail,
    meta: { requiresAuth: true, screenName: "Group Detail" },
  },
  // {
  //   path: "/groups/edit/:gslug/sub-groups",
  //   name: "groupSubGroup",
  //   component: SubGroups,
  //   meta: { requiresAuth: true },
  // },

  {
    path: "/kiosk",
    name: "kiosk",
    component: () =>
      import(
        /* webpackChunkName: "route-kiosk" */ "@/views/user/KioskView.vue"
      ),
    // No requiresAuth / requiresGuest – the view manages both states internally
    meta: { screenName: "Kiosk", kioskEntry: true },
  },
  {
    path: "/o/:token",
    name: "one-time-token-page",
    component: Ottr,
    meta: { requiresAuth: true, screenName: "One-Time Token" },
  },
  {
    path: "/groups/:gslug/devices",
    name: "group-devices",
    component: GroupDevices,
    meta: { requiresAuth: true, screenName: "Group Devices" },
    children: [
      {
        path: "/groups/:gslug/devices/:slug",
        name: "groupdevice",
        component: Device,
        meta: { requiresAuth: true, screenName: "Group Device Details" },
      },
    ],
  },
];
const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return { x: 0, y: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  logAuthEvent("auth.route_guard_started", routeGuardMetadata(to, from), {
    to,
    from,
  });
  let requiredPermission = true;
  if (to.meta.permissionsRequired) {
    for (let i = 0; i < to.meta.permissionsRequired.length; i++) {
      const per = to.meta.permissionsRequired[i];
      if (typeof per === "string") {
        if (!Vue.prototype.$userPermissions.permitted(per)) {
          requiredPermission = false;
        }
      } else if (Array.isArray(per)) {
        let trueOption = false;
        for (let j = 0; j < per.length; j++) {
          if (Vue.prototype.$userPermissions.permitted(per[j])) {
            trueOption = true;
            break;
          }
        }
        if (!trueOption) {
          requiredPermission = false;
        }
      }
    }
  }
  const homepage = "/";
  if (to.meta.requiresAuth && !Guard.validUser()) {
    const rejectionMetadata = routeGuardMetadata(to, from, {
      guardDecision: "redirect_login",
      rejectReason: "missing_authentication",
    });
    logRouteGuardRejection(logger, {
      reason: "missing_authentication",
      to,
      from,
    });
    logAuthEvent("auth.route_guard_rejected", rejectionMetadata, { to, from });
    return next({ path: "/login", replace: true });
  } else if (!requiredPermission) {
    logAuthEvent(
      "auth.route_guard_rejected",
      routeGuardMetadata(to, from, {
        guardDecision: "redirect_homepage",
        rejectReason: "missing_permission",
        redirectPath: homepage,
      }),
      { to, from }
    );
    // return next("/");
    return next({ path: homepage, replace: true });
  } else if (to.meta.requiresGuest && Guard.validUser()) {
    // return next("/");
    if (to.path === homepage) {
      logAuthEvent(
        "auth.route_guard_allowed",
        routeGuardMetadata(to, from, { guardDecision: "allow_homepage" }),
        { to, from }
      );
      return next();
    }
    logAuthEvent(
      "auth.route_guard_rejected",
      routeGuardMetadata(to, from, {
        guardDecision: "redirect_authenticated_guest",
        rejectReason: "already_authenticated",
        redirectPath: homepage,
      }),
      { to, from }
    );
    return next({ path: homepage, replace: true });
  }

  if (to.meta.requiresSolarActionsDevice) {
    const hasAccess = await hasSolarActionsAccess();
    if (!hasAccess) {
      logAuthEvent(
        "auth.route_guard_rejected",
        routeGuardMetadata(to, from, {
          guardDecision: "redirect_homepage",
          rejectReason: "missing_solar_actions_device",
          redirectPath: homepage,
        }),
        { to, from }
      );
      return next({ path: homepage, replace: true });
    }
  }

  // Block write-only pages entirely when running as a kiosk session
  if (to.meta.kioskBlocked) {
    const isKiosk = auth.isReadOnly();
    if (isKiosk) {
      logAuthEvent(
        "auth.route_guard_rejected",
        routeGuardMetadata(to, from, {
          guardDecision: "redirect_homepage",
          rejectReason: "kiosk_blocked_route",
          redirectPath: homepage,
        }),
        { to, from }
      );
      return next({ path: homepage, replace: true });
    }
  }

  // ── Landing page preference redirect ──────────────────────────────
  // When an authenticated user navigates to "/", treat it as the app landing
  // resolver and redirect to their configured landing page. The real dashboard
  // page lives at "/dashboard" so explicit dashboard navigation is never
  // intercepted by this preference redirect.
  if (to.path === "/" && Guard.validUser()) {
    try {
      const landingPage = store.getters["preferences/landingPage"];
      const pageRoutes = {
        dashboard: "/dashboard",
        devices: "/devices",
        groups: "/groups",
        map: "/map",
      };
      if (landingPage && pageRoutes[landingPage]) {
        logAuthEvent(
          "auth.route_guard_allowed",
          routeGuardMetadata(to, from, {
            guardDecision: "redirect_landing_page",
            landingPage,
          }),
          { to, from }
        );
        return next({ path: pageRoutes[landingPage], replace: true });
      }
    } catch (_) {
      // preferences not ready – fall through to dashboard
    }
  }

  logAuthEvent(
    "auth.route_guard_allowed",
    routeGuardMetadata(to, from, { guardDecision: "allow" }),
    { to, from }
  );
  return next();
});

export default router;
